import useSWR from "swr";

import SWR_KEYS from "@/constants/swr-keys";
import { getPublicImageUrl } from "@/helpers/image-url-helper";
import { IProductImage } from "@/interfaces/product-image.interface";
import productImageService from "@/services/product-images.service";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestsBaseReturn } from "@/types/requests.types";

export type UseGetProductImagesProps = {
  filters: IFilter<IProductImage>[];
  pageNumber: number;
  pageSize: number;
  enabled?: boolean;
};

export type UseGetProductImagesReturn = RequestsBaseReturn<PaginatedResponse<IProductImage> | null> & {
  mutate: () => Promise<any>;
};

type UseSwrGetProductImagesParams = [string, number, number, IFilter<IProductImage>[]];

const useGetProductImages = ({
  filters,
  pageNumber,
  pageSize,
  enabled = true,
}: UseGetProductImagesProps): UseGetProductImagesReturn => {
  const {
    data = null,
    isValidating: isLoading,
    error,
    mutate,
  } = useSWR<
    PaginatedResponse<IProductImage>,
    Error,
    UseSwrGetProductImagesParams | null
  >(
    enabled ? [SWR_KEYS.getProductImages, pageNumber, pageSize, filters] : null,
    async ([, page, size, filterList]) => {
      const response = await productImageService.filter(filterList, { 
        pageNumber: page, 
        pageSize: size 
      });
      
      // ✅ TRANSFORMAR imagens para incluir URL acessível
      if (response?.items) {
        response.items = response.items.map(image => {
          // 1️⃣ PRIORIDADE: publicUrl da API (se existir)
          let url = image.publicUrl;
          
          // 2️⃣ FALLBACK: Gerar publicUrl a partir do phisicalAddress
          if (!url && image.phisicalAddress) {
            url = getPublicImageUrl(image.phisicalAddress);
          }
          
          // 3️⃣ FALLBACK FINAL: preview (para imagens recém-uploaded)
          if (!url && image.preview) {
            url = image.preview;
          }
          
          return {
            ...image,
            url
          };
        });
      }
      
      return response;
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    }
  );

  return { data, isLoading, error, mutate };
};

export default useGetProductImages;
